import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Modal, Alert, Spin, Button } from "antd";
import Cookies from "universal-cookie";
import { useDispatch } from "react-redux";

import TaskInfoSection from "./TaskInfoSection";
import SpecialInstructionsEditor from "./SpecialInstructionsEditor";
import AnswerList from "./AnswerList";
import FooterActions from "./FooterActions";

import { updateAnswer } from "../../../../functions/task";
import { loaderReducer } from "../../../../components/toolkit/loader";
import { toast } from "react-toastify";
import { useCan } from "../../../../utils/permissions";

const TaskAnswerModal = ({
  arg: {
    isModalOpen,
    handleOk,
    handleCancel,
    selectedTask,
    specialInstructions,
    techName,
    tranId,
    customFields,
    setCustomFields,
    reconciliationStatus,
    isLoadingAnswers,
    answerFetchError,
  },
}) => {
  const dispatch = useDispatch();
  const cookies = new Cookies();
  const can = useCan();
  const canEditAnswer = can("answer", "update");

  const isWOCompleted = reconciliationStatus === "Completed";
  const [canEditTaskSurvey, setCanEditTaskSurvey] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [valueChange, setValueChange] = useState({ speacialInstructions: "" });

  const [localSerial, setLocalSerial] = useState("");
  const [localVersion, setLocalVersion] = useState("");

  // ✅ NEW: Track if user is in EDIT MODE
  const [isEditMode, setIsEditMode] = useState(false);

  const surveyTemplate = customFields?.surveyTemplate || customFields?.surveyId;

  const isTaskCompleted = selectedTask?.isCompleted === true ||
    selectedTask?.isCompleted === "true" ||
    selectedTask?.status === "completed";

  const hasAnswers = customFields?.answer && Array.isArray(customFields.answer) && customFields.answer.length > 0;

  useEffect(() => {
    if (isModalOpen) {
      console.log("[TaskAnswerModal] State Update:", {
        isModalOpen,
        isLoadingAnswers,
        hasError: !!answerFetchError,
        hasAnswers,
        answerCount: customFields?.answer?.length || 0,
        isEditMode,
      });
    }
  }, [isModalOpen, customFields, isLoadingAnswers, answerFetchError, hasAnswers, isEditMode]);

  useEffect(() => {
    setCanEditTaskSurvey(canEditAnswer && !isWOCompleted);
  }, [canEditAnswer, isWOCompleted]);

  useEffect(() => {
    if (isModalOpen && selectedTask) {
      setLocalSerial(selectedTask?.serialNumber || "");
      setLocalVersion(selectedTask?.custcolVersion || "");
      setIsEditMode(false); // ✅ Reset to view mode when modal opens
    }
  }, [isModalOpen, selectedTask]);

  // ✅ CHECK CONDITION FUNCTION (only used in EDIT mode)
  const checkCondition = useCallback((sourceQuestion, condition, answerMap) => {
    if (!sourceQuestion?.uuid || !condition) return false;

    const sourceAnswer = answerMap[sourceQuestion.uuid];
    const sourceValue = sourceAnswer?.answer || "";
    const conditionState = (condition.state || "").toLowerCase().trim();
    const conditionValue = condition.value;

    if (conditionState === "is equal to") {
      return String(sourceValue).toLowerCase() === String(conditionValue).toLowerCase();
    }

    if (conditionState === "filled") {
      return String(sourceValue).trim().length > 0;
    }

    return false;
  }, []);

  // ✅ BUILD QUESTION MAP
  const byId = useMemo(() => {
    const map = new Map();
    if (surveyTemplate?.customSurvey) {
      surveyTemplate.customSurvey.forEach((q) => {
        if (q.uuid) {
          map.set(q.uuid, q);
        }
      });
    }
    return map;
  }, [surveyTemplate]);

  const allAnswers = useMemo(() => {
    if (!customFields?.answer) {
      return [];
    }
    if (!Array.isArray(customFields.answer)) {
      return [];
    }
    return customFields.answer;
  }, [customFields]);

  // ✅ VISIBLE PATH - ONLY USED IN EDIT MODE
  const visiblePathInEditMode = useMemo(() => {
    if (!isEditMode || !surveyTemplate?.customSurvey?.length) {
      return [];
    }

    // Create answer map
    const answerMap = {};
    allAnswers.forEach(a => {
      if (a.uuid) answerMap[a.uuid] = a;
    });

    // Find questions that are not targeted by any condition (root questions)
    const targetedQuestions = new Set();
    surveyTemplate.customSurvey.forEach(q => {
      (q.conditions || []).forEach(cond => {
        if (cond.questionId) targetedQuestions.add(cond.questionId);
      });
    });

    const rootQuestions = surveyTemplate.customSurvey.filter(q =>
      q.uuid && !targetedQuestions.has(q.uuid)
    );

    // BFS to determine visible questions
    const visible = new Set();
    const queue = [...rootQuestions];

    while (queue.length > 0) {
      const question = queue.shift();
      if (!question.uuid || visible.has(question.uuid)) continue;

      visible.add(question.uuid);

      (question.conditions || []).forEach(condition => {
        if (!condition.questionId) return;

        const isConditionMet = checkCondition(question, condition, answerMap);
        if (isConditionMet) {
          const nextQuestion = byId.get(condition.questionId);
          if (nextQuestion && !visible.has(nextQuestion.uuid)) {
            queue.push(nextQuestion);
          }
        }
      });
    }

    return Array.from(visible);
  }, [allAnswers, surveyTemplate, byId, checkCondition, isEditMode]);

  // ✅ ANSWERS TO DISPLAY
  const answersToDisplay = useMemo(() => {
    if (!hasAnswers) return [];

    if (isEditMode) {
      // ✅ EDIT MODE: Show questions based on template logic
      const visibleSet = new Set(visiblePathInEditMode);

      // Start with existing answers that are visible
      const result = allAnswers.filter(answer =>
        answer.uuid && visibleSet.has(answer.uuid)
      );

      // Add missing answers for visible questions
      visiblePathInEditMode.forEach(uuid => {
        const exists = result.some(answer => answer.uuid === uuid);
        if (!exists) {
          const question = byId.get(uuid);
          if (question) {
            result.push({
              uuid: uuid,
              question: question.question || question.foot_photo || "",
              answer: "",
              image: null,
              geotag: null,
              type: question.type
            });
          }
        }
      });

      console.log("[TaskAnswerModal] EDIT MODE - Visible answers:", result.length);
      return result;
    } else {
      // ✅ VIEW MODE: Show ALL submitted answers as-is (no template logic)
      console.log("[TaskAnswerModal] VIEW MODE - Showing all answers:", allAnswers.length);
      return allAnswers;
    }
  }, [allAnswers, hasAnswers, isEditMode, visiblePathInEditMode, byId]);

  // ✅ HANDLE ANSWER CHANGE
  const handleAnswerChange = useCallback((uuid, newValue) => {
    if (!canEditTaskSurvey || !uuid || !isEditMode) return;

    setCustomFields(prev => {
      const currentAnswers = prev?.answer || [];
      const answerExists = currentAnswers.some(ans => ans.uuid === uuid);

      let updatedAnswers;
      if (answerExists) {
        updatedAnswers = currentAnswers.map(ans =>
          ans.uuid === uuid ? { ...ans, answer: newValue } : ans
        );
      } else {
        const question = byId.get(uuid);
        const newAnswer = {
          uuid: uuid,
          question: question?.question || question?.foot_photo || "",
          answer: newValue,
          image: null,
          geotag: null,
          type: question?.type
        };
        updatedAnswers = [...currentAnswers, newAnswer];
      }

      return {
        ...prev,
        answer: updatedAnswers
      };
    });
  }, [canEditTaskSurvey, setCustomFields, byId, isEditMode]);

  // ✅ HANDLE IMAGE UPLOAD
  const handleImageUpload = useCallback((uuid, file, imageUrl, geotag = "true") => {
    if (!canEditTaskSurvey || !uuid || !isEditMode) return;

    setCustomFields(prev => {
      const currentAnswers = prev?.answer || [];
      const answerExists = currentAnswers.some(ans => ans.uuid === uuid);

      let updatedAnswers;
      if (answerExists) {
        updatedAnswers = currentAnswers.map(ans =>
          ans.uuid === uuid ? {
            ...ans,
            image: imageUrl,
            file: file,
            geotag: geotag,
            answer: "Image uploaded"
          } : ans
        );
      } else {
        const question = byId.get(uuid);
        const newAnswer = {
          uuid: uuid,
          question: question?.question || question?.foot_photo || "",
          answer: "Image uploaded",
          image: imageUrl,
          file: file,
          geotag: geotag,
          type: question?.type
        };
        updatedAnswers = [...currentAnswers, newAnswer];
      }

      return {
        ...prev,
        answer: updatedAnswers
      };
    });
  }, [canEditTaskSurvey, setCustomFields, byId, isEditMode]);

  // ✅ HANDLE IMAGE REMOVAL
  const handleImageRemove = useCallback((answer) => {
    if (!canEditTaskSurvey || !answer?.uuid || !isEditMode) return;

    setCustomFields(prev => {
      if (!prev?.answer) return prev;

      const updatedAnswers = prev.answer.map(ans =>
        ans.uuid === answer.uuid
          ? {
            ...ans,
            image: null,
            file: null,
            geotag: "false",
            answer: ""
          }
          : ans
      );

      return {
        ...prev,
        answer: updatedAnswers
      };
    });
  }, [canEditTaskSurvey, setCustomFields, isEditMode]);

  // ✅ CHECK REQUIRED FIELDS (only in edit mode)
  const hasRequiredMissing = useMemo(() => {
    if (!isEditMode) return false;

    return answersToDisplay.some(answer => {
      const question = byId.get(answer.uuid);
      if (!question) return false;

      const isRequired = String(question.require_response || question.requireResponse || "")
        .toLowerCase() === "true";

      if (!isRequired) return false;

      if (question.type === "image") {
        return !answer.image && !answer.answer;
      }

      return !answer.answer || String(answer.answer).trim().length === 0;
    });
  }, [answersToDisplay, byId, isEditMode]);

  // ✅ SAVE CHANGES
  const handleSaveChanges = async () => {
    if (isWOCompleted) {
      toast.error("Cannot edit tasks for completed work orders");
      return;
    }

    if (hasRequiredMissing) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSaving(true);
    dispatch(loaderReducer(true));

    try {
      // Filter to only save visible answers in edit mode
      const answersToSave = isEditMode ? answersToDisplay : allAnswers;

      const ansData = {
        surveyId: customFields?.surveyId?._id || customFields?.surveyId,
        userId: customFields?.userId,
        taskUniqueId: selectedTask?.taskUniqueId,
        taskId: selectedTask?._id,
        tranId: selectedTask?.tranId,
        serialNumber: localSerial,
        custcolVersion: localVersion,
        specialInstructions: valueChange.speacialInstructions || specialInstructions || "",
        answer: answersToSave.map((item) => ({
          uuid: item.uuid,
          question: item.question,
          answer: item.answer?.toString()?.trim() || "",
          image: item.image || null,
          geotag: item.geotag || "false",
        })),
      };

      const result = await updateAnswer(selectedTask._id, ansData);
      console.log("[TaskAnswerModal] Update successful:", result);

      toast.success("Survey updated successfully");
      setIsEditMode(false); // Exit edit mode after save
      handleOk();
    } catch (error) {
      console.error("[TaskAnswerModal] Save error:", error);
      toast.error("Update failed: " + (error.message || "Unknown error"));
    } finally {
      dispatch(loaderReducer(false));
      setIsSaving(false);
    }
  };

  // ✅ RENDER LOADING STATE
  if (isLoadingAnswers) {
    return (
      <Modal
        title="Task Details"
        open={isModalOpen}
        onCancel={handleCancel}
        footer={null}
        width={800}
      >
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '300px',
          padding: '40px 20px'
        }}>
          <Spin size="large" />
          <p style={{ marginTop: '20px', fontSize: '16px', color: '#666' }}>
            Loading task details...
          </p>
        </div>
      </Modal>
    );
  }

  // ✅ RENDER ERROR STATE
  if (answerFetchError && isTaskCompleted) {
    return (
      <Modal
        title="Task Details"
        open={isModalOpen}
        onCancel={handleCancel}
        footer={null}
        width={800}
      >
        <Alert
          message="Unable to Load Task Data"
          description={answerFetchError}
          type="error"
          showIcon
        />
      </Modal>
    );
  }

  // ✅ RENDER MAIN CONTENT
  return (
    <Modal
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>
            Task Details
            {isWOCompleted && (
              <span style={{ color: "#d00", marginLeft: '10px', fontSize: '14px' }}>
                (Read Only - Work Order Completed)
              </span>
            )}
            {isEditMode && (
              <span style={{ color: "#1890ff", marginLeft: '10px', fontSize: '14px' }}>
                (Edit Mode)
              </span>
            )}
          </span>
        </div>
      }
      open={isModalOpen}
      onOk={handleOk}
      onCancel={handleCancel}
      footer={null}
      width={800}
      destroyOnClose
    >
      <TaskInfoSection
        tranId={tranId}
        techName={techName}
        serialNumber={localSerial}
        versionAssigned={localVersion}
        onSerialNumberChange={(e) => setLocalSerial(e.target.value)}
        onVersionChange={(e) => setLocalVersion(e.target.value)}
        canEdit={canEditTaskSurvey && isEditMode}
      />

      <SpecialInstructionsEditor
        value={valueChange.speacialInstructions || specialInstructions}
        onChange={(val) => setValueChange(prev => ({ ...prev, speacialInstructions: val }))}
        canEdit={canEditTaskSurvey && isEditMode}
      />

      {/* ✅ SHOW SURVEY SECTION IF WE HAVE ANSWERS */}
      {hasAnswers && (
        <div className="mt-4">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h4 style={{ margin: 0 }}>Survey Answers</h4>

            {/* ✅ EDIT BUTTON - Only show if can edit and not in edit mode */}
            {canEditTaskSurvey && !isEditMode && (
              <Button
                type="primary"
                onClick={() => setIsEditMode(true)}
              >
                Edit Answers
              </Button>
            )}

            {/* ✅ CANCEL EDIT BUTTON - Only show in edit mode */}
            {isEditMode && (
              <Button
                onClick={() => setIsEditMode(false)}
              >
                Cancel Edit
              </Button>
            )}
          </div>

          {answersToDisplay.length === 0 ? (
            <Alert
              message="No answers to display"
              type="info"
              showIcon
            />
          ) : (
            <AnswerList
              answers={answersToDisplay}
              onAnswerChange={handleAnswerChange}
              onImageUpload={handleImageUpload}
              onImageRemove={handleImageRemove}
              canEdit={canEditTaskSurvey && isEditMode}  // ✅ Only editable in edit mode
              surveyTemplate={surveyTemplate}
            />
          )}
        </div>
      )}

      {/* ✅ FOOTER - Only show save button in edit mode */}
      {isEditMode && (
        <FooterActions
          onSave={handleSaveChanges}
          isSaving={isSaving}
          hasRequiredMissing={hasRequiredMissing}
          canEdit={canEditTaskSurvey}
          isWOCompleted={isWOCompleted}
        />
      )}
    </Modal>
  );
};

export default TaskAnswerModal;
